import type { DeliveryChannel, DeliveryStatus } from '@pm/schemas';
import {
  Badge,
  type BadgeTone,
  Button,
  EmptyState,
  ErrorState,
  LabelCaps,
  Select,
  SkeletonRows,
  Tabs,
} from '@pm/ui';
import { Inbox, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { useDeliveries, useIntegrationsHealth, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const STATUS_TONE: Record<DeliveryStatus, BadgeTone> = {
  PENDING: 'info',
  SENT: 'success',
  FAILED: 'warning',
  DEAD: 'danger',
  SKIPPED: 'neutral',
};
const STATUS_LABEL: Record<DeliveryStatus, string> = {
  PENDING: 'pendente',
  SENT: 'enviada',
  FAILED: 'falhou',
  DEAD: 'descartada',
  SKIPPED: 'ignorada',
};
const CHANNEL_LABEL: Record<DeliveryChannel, string> = {
  EMAIL: 'E-mail',
  WHATSAPP: 'WhatsApp',
  TELEGRAM: 'Telegram',
  CALENDAR: 'Calendário',
};

/** NOTIFICATIONS §11-§12 — saúde dos canais e log de entregas (dead-letter) da organização. */
export function IntegrationsSection() {
  const toast = useToast();
  const { data: health } = useIntegrationsHealth();
  const [tab, setTab] = useState<'problems' | 'all'>('problems');
  const [channel, setChannel] = useState<'' | DeliveryChannel>('');
  const { data, isLoading, isError, error, refetch } = useDeliveries({
    status: tab === 'problems' ? ['FAILED', 'DEAD', 'SKIPPED'] : undefined,
    channel: channel || undefined,
  });
  const retry = useInvalidating(
    (id: string) => api.post(`/integrations/deliveries/${id}/retry`),
    [['integration-deliveries']],
  );
  const rows = data?.data ?? [];

  return (
    <div className="flex flex-col gap-gap">
      <div className="grid gap-gap sm:grid-cols-4">
        {health
          ? [
              ['E-mail (SMTP)', health.email],
              ['WhatsApp (Evolution)', health.whatsapp],
              ['Telegram (bot)', health.telegram],
              [
                'Calendário',
                health.calendar.GOOGLE || health.calendar.MICROSOFT || health.calendar.CALDAV,
              ],
            ].map(([label, ok]) => (
              <div
                key={String(label)}
                className="flex items-center gap-2 rounded-md border border-border bg-surface-1 px-3 py-2"
              >
                <span className={`size-2 rounded-full ${ok ? 'bg-success' : 'bg-text-3'}`} />
                <span className="text-sm">{label}</span>
                <span className="ml-auto font-mono text-[10px] uppercase text-text-3">
                  {ok ? 'ok' : 'off'}
                </span>
              </div>
            ))
          : null}
      </div>
      <p className="text-xs text-text-3">
        E-mail, WhatsApp e Telegram são configurados na aba Parâmetros (ou, como reserva, nas
        variáveis de ambiente do servidor). Calendários Google/Microsoft usam variáveis de ambiente.
        Sem configuração, os envios ficam como ignorados e podem ser reprocessados depois.
      </p>
      <div className="flex items-center gap-2">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'problems', label: 'Problemas' },
            { value: 'all', label: 'Todas' },
          ]}
        />
        <Select
          value={channel}
          onChange={(e) => setChannel(e.target.value as '' | DeliveryChannel)}
          className="ml-auto h-control-sm w-[160px] text-sm"
          aria-label="Canal"
        >
          <option value="">Todos os canais</option>
          {(Object.keys(CHANNEL_LABEL) as DeliveryChannel[]).map((c) => (
            <option key={c} value={c}>
              {CHANNEL_LABEL[c]}
            </option>
          ))}
        </Select>
      </div>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Nada por aqui"
            description={
              tab === 'problems'
                ? 'Nenhuma entrega com problema.'
                : 'Nenhuma entrega registrada ainda.'
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Canal</th>
                <th className="h-8 px-3 text-left font-medium">Assunto</th>
                <th className="h-8 px-3 text-left font-medium">Para</th>
                <th className="h-8 px-3 text-left font-medium">Status</th>
                <th className="h-8 px-3 text-left font-medium">Erro</th>
                <th className="h-8 px-3 text-right font-medium">Quando</th>
                <th className="h-8 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className="h-row border-b border-border text-sm last:border-b-0 hover:bg-surface-2"
                >
                  <td className="px-3">
                    <Badge mono={false} tone="neutral">
                      {CHANNEL_LABEL[d.channel]}
                    </Badge>
                  </td>
                  <td className="max-w-[280px] truncate px-3" title={d.subject ?? undefined}>
                    {d.subject ?? <span className="text-text-3">—</span>}
                    <span className="ml-2 font-mono text-[10px] text-text-3">
                      {d.notification_type}
                    </span>
                  </td>
                  <td className="px-3">
                    <span className="text-text-2">{d.user?.name}</span>
                    {d.target ? (
                      <div className="font-mono text-xs text-text-3">{d.target}</div>
                    ) : null}
                  </td>
                  <td className="px-3">
                    <Badge tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                    {d.attempts > 0 ? (
                      <span className="ml-1 font-mono text-[10px] text-text-3">{d.attempts}×</span>
                    ) : null}
                  </td>
                  <td
                    className="max-w-[260px] truncate px-3 text-xs text-text-3"
                    title={d.last_error ?? undefined}
                  >
                    {d.last_error ?? ''}
                  </td>
                  <td className="px-3 text-right font-mono text-xs text-text-3">
                    {fmtRelative(d.sent_at ?? d.created_at)}
                  </td>
                  <td className="px-3 text-right">
                    {d.status !== 'SENT' && d.status !== 'PENDING' && d.channel !== 'CALENDAR' ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Reprocessar"
                        loading={retry.isPending}
                        onClick={() =>
                          retry.mutate(d.id, {
                            onSuccess: () => toast.success('Reenfileirada'),
                            onError: (e) => toast.error(e),
                          })
                        }
                      >
                        <RotateCcw />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data ? <LabelCaps>{data.meta.total} entregas</LabelCaps> : null}
    </div>
  );
}
